import { useEffect, useRef, useState } from 'react';
import { Dialog, Disclosure, Popover, PopoverButton, PopoverPanel, PopoverGroup, PopoverBackdrop, Transition } from '@headlessui/react';

const LONG_PRESS_MS = 450;
const CHIP_MAX_W_PX = 100;

const SearchArea = (props) => {
  const [searchTerms, setSearchTerms] = useState([]);
  const [activeTip, setActiveTip] = useState(null); // term string or null
  const pressTimerRef = useRef(null);

  useEffect(() => {
    if (props?.search?.length > 0) {
      props.setSearchText('Search');
    } else if (props.searchMade) {
      props.setSearchText('Clear');
    }
  }, [props.search, props.searchMade]);

  useEffect(() => {
    setSearchTerms([]);
    setActiveTip(null);
  }, [props.resetVersion]);

  function addTerm(raw) {
    const term = String(raw || '').trim().toLowerCase();
    if (!term) return;

    setSearchTerms((prev) => {
      if (prev.includes(term)) return prev;
      return [...prev, term];
    });
  }

  function removeTerm(term) {
    setSearchTerms((prev) => prev.filter((t) => t !== term));
    setActiveTip((prev) => (prev === term ? null : prev));
  }

  function clearPressTimer() {
    if (pressTimerRef.current) {
      window.clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  }

  function onChipPointerDown(term) {
    clearPressTimer();
    pressTimerRef.current = window.setTimeout(() => {
      setActiveTip(term);
    }, LONG_PRESS_MS);
  }

  function onChipPointerUp() {
    clearPressTimer();
  }

  function onChipPointerCancel() {
    clearPressTimer();
    setActiveTip(null);
  }

  return (
    <div className="w-full">
      

      {/* Search form (separate, full width) */}
      <form
        className="w-full"
        onSubmit={(e) => {
          e.preventDefault();
          if (!props.searchMade && props.search === '') return;
          props.filterData(props.search);
          props.setSearchMade?.(true);
          addTerm(props.search);
        }}
      >
        <div className="flex w-full flex-row items-center gap-2">
          <input
            id="search-value"
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            placeholder="Enter a keyword."
            name="search"
            type="text"
            onChange={(e) => props.setSearch(e.target.value.toLowerCase())}
            value={props.search}
          />

          <button 
            className="rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-gray-800"
            type="submit"
          >
            Search
          </button>

          <button
            type="button"
            className="rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-gray-800"
            onClick={() => {
              props.filterData('');
              setSearchTerms([]);
              props.setSearch('');
              props.setSearchMade?.(false);
              setActiveTip(null);
            }}
          >
            Clear
          </button>
        </div>
        {/* Search terms (centered) */}
      {searchTerms.length > 0 && (
        <div
          className="mt-2 mx-auto"
          style={{
            maxWidth: '85vw',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div className="flex flex-wrap justify-center items-start gap-2">
            {searchTerms.map((term) => (
              <span key={term} className="relative">
                <span
                  className="search-term-chip inline-flex items-center gap-2"
                  style={{ maxWidth: `${CHIP_MAX_W_PX}px` }}
                  onPointerDown={() => onChipPointerDown(term)}
                  onPointerUp={onChipPointerUp}
                  onPointerLeave={onChipPointerCancel}
                  onPointerCancel={onChipPointerCancel}
                >
                  <span className="min-w-0 flex-1 truncate text-left" title={term}>
                    {term}
                  </span>

                  <button
                    type="button"
                    className="search-term-remove shrink-0"
                    aria-label={`Remove ${term}`}
                    onClick={() => removeTerm(term)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-3 w-3">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>

                  </button>
                </span>

                {/* Mobile long-press tooltip */}
                {activeTip === term && (
                  <div
                    className="absolute left-0 top-full z-50 mt-1 w-max max-w-[260px] rounded-md bg-gray-900 px-2 py-1 text-xs text-white shadow-lg"
                    role="tooltip"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="break-words">{term}</div>
                    <button
                      type="button"
                      className="mt-1 text-xs underline"
                      onClick={() => setActiveTip(null)}
                    >
                      Close
                    </button>
                  </div>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
      </form>
    </div>
  );
};

export default SearchArea;
